import React, { useState } from 'react';
import './ExportButtons.css';
import { exportChordPro, exportText } from '../utils/chordProExporter';
import { exportPDF } from '../utils/pdfExporter';
import { downloadIReal } from '../utils/irealExporter';
import { exportSrt } from '../utils/srtExporter';
import { buildShareUrl } from '../utils/shareUrl';
import { transposeChord, chordShapeForCapo, accidentalPreference, simplifyChord } from '../utils/chordTranspose';

/**
 * Apply transposition/capo to alignment so that
 * the exported files show the same chords as the on-screen display.
 */
function buildExportAlignment({ alignment, transposition, capo, songKey, simplified, lyrics }) {
  const pref = accidentalPreference(songKey);
  const semitones = transposition || 0;

  // Step 1: apply transpose + capo to every chord
  const transformed = (alignment || []).map((entry) => {
    const { chord } = entry || {};
    if (!entry || !chord) return { ...entry };
    const base = simplified ? simplifyChord(chord) : chord;
    const transposed = transposeChord(base, semitones, pref);
    const displayed = capo > 0 ? chordShapeForCapo(transposed, capo) : transposed;
    return {
      ...entry,
      chord: displayed,
    };
  });

  // Preserve canonical anchors/spacers as-is; no synthetic carry-forward insertion.
  return transformed;
}

function ExportButtons({ title, songKey, capo, lyrics, alignment, timedLyrics, transposition, simplified, bpm, strumPattern }) {
  const [copied, setCopied] = useState(false);
  const exportAlignment = buildExportAlignment({ alignment, transposition, capo, songKey, simplified, lyrics });

  const handleChordPro = () => {
    exportChordPro({ title, key: songKey, capo, lyrics, alignment: exportAlignment });
  };

  const handlePDF = () => {
    exportPDF({ title, key: songKey, capo, bpm, lyrics, alignment: exportAlignment, transposition, strumPattern });
  };

  const handleText = () => {
    exportText({ title, key: songKey, capo, bpm, lyrics, alignment: exportAlignment, transposition });
  };

  const handleSrt = () => {
    exportSrt({ title, lyrics, timedLyrics });
  };

  const handleIReal = () => {
    // Build a flat alignment for iReal: list of {line, chords:{wi: chord}} objects
    const lines = (lyrics || '').split('\n');
    const flatAlignment = lines.map((line, li) => {
      const chords = {};
      exportAlignment.filter(a => a.lineIndex === li).forEach(a => {
        chords[a.wordIndex] = a.chord;
      });
      return { line, chords };
    });
    downloadIReal({ title, songKey, alignment: flatAlignment, transposition: 0, capo: 0 });
  };

  const handleShareLink = async () => {
    try {
      const url = await buildShareUrl({ title, lyrics, alignment, songKey, bpm, transposition, capo });
      if (!url) return;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (err) {
      const legacyUrl = (() => {
        try {
          const encoded = btoa(encodeURIComponent(JSON.stringify({ title, lyrics, alignment, songKey, bpm, transposition, capo })));
          const url = new URL(window.location.href);
          url.search = '';
          url.searchParams.set('song', encoded);
          return url.toString();
        } catch {
          return '';
        }
      })();
      if (legacyUrl) prompt('Copy this link:', legacyUrl);
    }
  };

  const disabled = !lyrics || !alignment || alignment.length === 0;
  const captionDisabled = !lyrics || !timedLyrics || timedLyrics.length === 0;

  return (
    <div className="export-buttons">
      <button
        className="export-btn export-btn--text"
        onClick={handleText}
        disabled={disabled}
        title="Download as plain text (.txt)"
      >
        ↓ Text
      </button>
      <button
        className="export-btn export-btn--chordpro"
        onClick={handleChordPro}
        disabled={disabled}
        title="Download as ChordPro (.cho)"
      >
        ↓ ChordPro
      </button>
      <button
        className="export-btn export-btn--pdf"
        onClick={handlePDF}
        disabled={disabled}
        title="Download as PDF"
      >
        ↓ PDF
      </button>
      <button
        className="export-btn export-btn--srt"
        onClick={handleSrt}
        disabled={captionDisabled}
        title="Download timed captions as SubRip (.srt)"
      >
        ↓ SRT
      </button>
      <button
        className="export-btn export-btn--ireal"
        onClick={handleIReal}
        disabled={disabled}
        title="Download as iReal Pro chart (.irealb)"
      >
        ↓ iReal
      </button>
      <button
        className={`export-btn export-btn--share${copied ? ' copied' : ''}`}
        onClick={handleShareLink}
        disabled={disabled}
        title="Copy shareable link to this chord sheet"
      >
        {copied ? '✓ Copied!' : '🔗 Share'}
      </button>
    </div>
  );
}

export default ExportButtons;
